import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged, 
  createUserWithEmailAndPassword, 
  UserCredential, 
  User as FirebaseUser, 
  setPersistence, 
  browserLocalPersistence,
  sendEmailVerification,
  applyActionCode,
} from 'firebase/auth';
import { auth } from '../firebase';
import { logger } from '../shared/logger';
import { getFirestore, doc, getDoc, collection, getDocs, setDoc } from 'firebase/firestore';
import { Facility } from '../api_types';

interface BusinessData {
  businessId: string;
  facilityId?: string;
  name?: string;
  phone_number?: string;
  role?: string;
}

interface AuthContextType {
  user: User | null;
  currentUser: FirebaseUser | null;
  loading: boolean;
  userDataLoading: boolean;
  businessData: BusinessData | null;
  businessFacilities: Facility[];
  login: (email: string, password: string) => Promise<void>;
  businessLogin: (email: string, password: string) => Promise<void>;
  memberLogin: (email: string, password: string) => Promise<void>;
  staffLogin: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  signup: (email: string, password: string) => Promise<UserCredential>;
  refreshUserData: () => Promise<void>;
  sendVerificationEmail: () => Promise<void>;
  verifyEmail: (oobCode: string) => Promise<void>;
  isEmailVerified: boolean;
  updateUserProfile: (updatedData: Partial<User>) => Promise<void>;
}

export type UserRole = 'admin' | 'business' | 'member' | 'staff';

export interface User {
  uid: string;
  email: string;
  role: 'admin' | 'business' | 'member' | 'staff';
  facilityId?: string | null;
  name?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  stripeSubscriptionId?: string;
  businessId?: string;
}

export interface BusinessUser extends User {
  role: 'business';
  facilityId: string;
  facilityName?: string;
}

export interface AdminUser extends User {
  role: 'admin';
}

export interface MemberUser extends User {
  role: 'member';
}

export interface StaffUser extends User {
  role: 'staff';
  facilityId: string;
  facilityName?: string;
} 

const AuthContext = createContext<AuthContextType>({
  user: null,
  currentUser: null,
  loading: true,
  userDataLoading: true,
  businessData: null,
  businessFacilities: [],
  login: async () => {},
  businessLogin: async () => {},
  memberLogin: async () => {},
  staffLogin: async () => {},
  logout: async () => {},
  signup: async () => { throw new Error('AuthContext not initialized'); },
  refreshUserData: async () => {},
  sendVerificationEmail: async () => {},
  verifyEmail: async () => {},
  isEmailVerified: false,
  updateUserProfile: async () => {}
});

export const useAuth = () => useContext(AuthContext);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [currentUser, setCurrentUser] = useState<FirebaseUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [userDataLoading, setUserDataLoading] = useState(true);
  const [businessData, setBusinessData] = useState<BusinessData | null>(null);
  const [businessFacilities, setBusinessFacilities] = useState<Facility[]>([]);
  const [isEmailVerified, setIsEmailVerified] = useState(false);

  // Function to fetch and set user data
  const fetchAndSetUserData = async (firebaseUser: FirebaseUser) => {
    try {
      const db = getFirestore();
      logger.info('Fetching user data for:', { 
        uid: firebaseUser.uid,
        email: firebaseUser.email 
      });
      
      let userData: User = {
        uid: firebaseUser.uid,
        email: firebaseUser.email || '',
        name: firebaseUser.displayName || '',
        role: 'member'  // Default role, will be updated if found
      };

      // First check if they're a staff user in any business
      const businessesSnapshot = await getDocs(collection(db, 'businesses'));
      for (const businessDoc of businessesSnapshot.docs) {
        const businessId = businessDoc.id;
        
        // Check for staff user first
        const staffUserDoc = await getDoc(doc(db, `businesses/${businessId}/staff_users`, firebaseUser.uid));
        if (staffUserDoc.exists()) {
          const staffData = staffUserDoc.data();
          logger.info('Found staff user data:', { 
            businessId,
            data: staffData 
          });
          
          // Set the business ID in localStorage
          localStorage.setItem('businessId', businessId);
          localStorage.setItem('facilityId', staffData.facility_id);
          
          userData = {
            ...userData,
            role: 'staff',
            name: staffData.name || userData.name,
            businessId: businessId,
            facilityId: staffData.facility_id,
            phone: staffData.phone_number
          };
          setUser(userData);
          return userData;
        }

        // If not a staff user, check if they're a business user
        const businessUserDoc = await getDoc(doc(db, `businesses/${businessId}/business_users`, firebaseUser.uid));
        if (businessUserDoc.exists()) {
          const businessDataDoc = businessUserDoc.data();
          logger.info('Found business user data:', { 
            businessId,
            data: businessDataDoc 
          });
          userData = {
            ...userData,
            role: 'business',
            name: businessDataDoc.name || userData.name,
            businessId: businessId
          };
          setUser(userData);
          setBusinessData({ 
            businessId: businessId,
            ...businessDataDoc
          });
          // Fetch all facilities for this business
          const facilitiesSnapshot = await getDocs(collection(db, `businesses/${businessId}/facilities`));
          const facilities = facilitiesSnapshot.docs.map(facilityDoc => ({
            id: facilityDoc.id,
            ...facilityDoc.data()
          })) as Facility[];
          setBusinessFacilities(facilities);
          return userData;
        }
      }

      // If no staff or business user found, keep as member
      setUser(userData);
      return userData;
    } catch (error) {
      logger.error('Error fetching user data:', { 
        error: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
        uid: firebaseUser.uid,
        email: firebaseUser.email
      });
      // Set basic user data even if Firestore fetch fails
      const basicUserData: User = {
        uid: firebaseUser.uid,
        email: firebaseUser.email || '',
        name: firebaseUser.displayName || '',
        role: 'member'
      };
      setUser(basicUserData);
      return basicUserData;
    }
  };

  // Update the auth state change handler
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      logger.info('Auth state changed', { email: firebaseUser?.email });
      setCurrentUser(firebaseUser);
      setUserDataLoading(true);
      setIsEmailVerified(firebaseUser?.emailVerified || false);

      if (firebaseUser) {
        try {
          // Force refresh the user's token to get latest verification status
          await firebaseUser.reload();
          setIsEmailVerified(firebaseUser.emailVerified);
          
          await fetchAndSetUserData(firebaseUser);
        } catch (error) {
          logger.error('Error in auth state change', { error: error instanceof Error ? error.message : String(error) });
          // Set basic user data even if Firestore fetch fails
          setUser({
            uid: firebaseUser.uid,
            email: firebaseUser.email || '',
            name: firebaseUser.displayName || '',
            role: 'member'
          });
        }
      } else {
        setUser(null);
        setBusinessData(null);
      }
      
      setUserDataLoading(false);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Update the refreshUserData function
  const refreshUserData = async () => {
    if (!currentUser) {
      logger.warn('Cannot refresh user data: No current user');
      return;
    }
    
    try {
      setUserDataLoading(true);
      logger.info('Starting user data refresh', { 
        uid: currentUser.uid,
        email: currentUser.email,
        emailVerified: currentUser.emailVerified 
      });

      // Force refresh the user's token
      await currentUser.reload();
      logger.info('User reloaded', { 
        uid: currentUser.uid,
        email: currentUser.email,
        emailVerified: currentUser.emailVerified 
      });

      setIsEmailVerified(currentUser.emailVerified);
      logger.info('Email verification status updated', { 
        isEmailVerified: currentUser.emailVerified 
      });

      await fetchAndSetUserData(currentUser);
    } catch (error) {
      logger.error('Error refreshing user data:', { 
        error: error instanceof Error ? error.message : String(error),
        uid: currentUser.uid,
        email: currentUser.email,
        emailVerified: currentUser.emailVerified 
      });
    } finally {
      setUserDataLoading(false);
    }
  };

  const sendVerificationEmail = async () => {
    if (!currentUser) {
      // If no current user, try to get it from auth
      const user = auth.currentUser;
      if (!user) {
        throw new Error('No user is currently signed in');
      }
      setCurrentUser(user);
      await sendEmailVerification(user);
      logger.info('Verification email sent successfully');
    } else {
      await sendEmailVerification(currentUser);
      logger.info('Verification email sent successfully');
    }
  };

  const verifyEmail = async (oobCode: string) => {
    try {
      await applyActionCode(auth, oobCode);
      setIsEmailVerified(true);
      logger.info('Email verified successfully');
    } catch (error) {
      logger.error('Error verifying email:', { error: error instanceof Error ? error.message : String(error) });
      throw error;
    }
  };

  const businessLogin = async (email: string, password: string) => {
    try {
      await setPersistence(auth, browserLocalPersistence);
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;
      const db = getFirestore();
      
      logger.info('Starting business user lookup', { 
        uid: firebaseUser.uid,
        email: firebaseUser.email 
      });
      
      // Check if they're a business user in any business
      const businessesSnapshot = await getDocs(collection(db, 'businesses'));
      let businessUserFound = false;
      
      for (const businessDoc of businessesSnapshot.docs) {
        const businessId = businessDoc.id;
        
        // Check business users
        const businessUserDoc = await getDoc(doc(db, `businesses/${businessId}/business_users`, firebaseUser.uid));
        if (businessUserDoc.exists()) {
          const businessDataDoc = businessUserDoc.data();
          logger.info('Found business user data:', { 
            businessId,
            data: businessDataDoc 
          });

          // Create user object with all necessary data
          const userData: User = {
            uid: firebaseUser.uid,
            email: firebaseUser.email!,
            role: 'business',
            facilityId: null, // We'll set this when they select a facility
            name: businessDataDoc.name || firebaseUser.displayName || undefined,
            phone: businessDataDoc.phone_number,
            businessId: businessId
          };

          // Set both user and business data
          setUser(userData);
          setBusinessData({ 
            businessId: businessId,
            ...businessDataDoc
          });

          // Fetch all facilities for this business
          const facilitiesSnapshot = await getDocs(collection(db, `businesses/${businessId}/facilities`));
          const facilities = facilitiesSnapshot.docs.map(facilityDoc => ({
            id: facilityDoc.id,
            ...facilityDoc.data()
          })) as Facility[];
          setBusinessFacilities(facilities);

          businessUserFound = true;
          break;
        }
      }

      
      if (!businessUserFound) {
        throw new Error('No business account found for this email');
      }

      // Wait for state updates to complete
      await new Promise(resolve => setTimeout(resolve, 100));
      
      logger.info('Successfully completed business login');
    } catch (error) {
      logger.error('Business login error:', { 
        error: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined
      });
      
      if (error instanceof Error) {
        if (error.message.includes('auth/invalid-credential')) {
          throw new Error('Invalid email or password');
        } else if (error.message.includes('auth/too-many-requests')) {
          throw new Error('Too many failed login attempts. Please try again later.');
        } else if (error.message.includes('auth/network-request-failed')) {
          throw new Error('Network error. Please check your connection and try again.');
        } else if (error.message.includes('auth/user-disabled')) {
          throw new Error('This account has been disabled. Please contact support.');
        }
      }
      
      throw error;
    }
  };

  const memberLogin = async (email: string, password: string) => {
    try {
      await setPersistence(auth, browserLocalPersistence);
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;
      const db = getFirestore();

      logger.info('Starting member login search', { 
        uid: firebaseUser.uid,
        email: firebaseUser.email 
      });

      // First try to find the user as a member in any business's facilities
      const businessesSnapshot = await getDocs(collection(db, 'businesses'));
      let memberFound = false;

      // Create a batch of promises to check all businesses and facilities
      const searchPromises = businessesSnapshot.docs.map(async (businessDoc) => {
        const businessId = businessDoc.id;
        const facilitiesSnapshot = await getDocs(collection(db, `businesses/${businessId}/facilities`));
        
        // Check each facility in parallel
        const facilityPromises = facilitiesSnapshot.docs.map(async (facilityDoc) => {
          const facilityId = facilityDoc.id;
          const memberDoc = await getDoc(doc(db, `businesses/${businessId}/facilities/${facilityId}/users`, firebaseUser.uid));
          
          if (memberDoc.exists()) {
            const memberData = memberDoc.data();
            logger.info('Found member data:', { 
              businessId,
              facilityId,
              data: memberData 
            });

            // Set the IDs in localStorage first
            localStorage.setItem('facilityId', facilityId);
            localStorage.setItem('businessId', businessId);
            
            // Create the user object
            const userData = {
              uid: firebaseUser.uid,
              email: firebaseUser.email!,
              role: 'member' as const,
              facilityId: facilityId,
              businessId: businessId,
              name: memberData.name || firebaseUser.displayName || undefined,
              firstName: memberData.firstName,
              lastName: memberData.lastName,
              phone: memberData.phone
            };

            // Set the user state
            setUser(userData);
            memberFound = true;
            return { found: true, userData };
          }
          return { found: false };
        });

        const facilityResults = await Promise.all(facilityPromises);
        const foundResult = facilityResults.find(result => result.found);
        if (foundResult?.found) {
          return foundResult;
        }
      });

      const results = await Promise.all(searchPromises);
      const foundMember = results.find(result => result?.found);

      if (foundMember?.found) {
        logger.info('Member login successful', { 
          uid: firebaseUser.uid,
          email: firebaseUser.email 
        });
        return;
      }

      // If member not found, check if they're a business user
      if (!memberFound) {
        logger.info('Member not found, checking business user status', { 
          uid: firebaseUser.uid,
          email: firebaseUser.email 
        });

        const businessUserPromises = businessesSnapshot.docs.map(async (businessDoc) => {
          const businessId = businessDoc.id;
          const businessUserDoc = await getDoc(doc(db, `businesses/${businessId}/business_users`, firebaseUser.uid));
          return { businessId, exists: businessUserDoc.exists() };
        });

        const businessResults = await Promise.all(businessUserPromises);
        const isBusinessUser = businessResults.some(result => result.exists);

        if (isBusinessUser) {
          throw new Error('This account is a business account. Please log in through the business portal.');
        }
      }

      // If we get here and no member was found, set basic member data
      if (!memberFound) {
        logger.warn('No member data found for user', { 
          uid: firebaseUser.uid,
          email: firebaseUser.email 
        });
        
        setUser({
          uid: firebaseUser.uid,
          email: firebaseUser.email!,
          role: 'member',
          name: firebaseUser.displayName || undefined
        });
      }
    } catch (error) {
      logger.error('Member login error:', { 
        error: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined
      });
      
      if (error instanceof Error) {
        if (error.message.includes('business account')) {
          throw error;
        } else if (error.message.includes('auth/invalid-credential')) {
          throw new Error('Invalid email or password');
        } else if (error.message.includes('auth/too-many-requests')) {
          throw new Error('Too many failed login attempts. Please try again later.');
        } else if (error.message.includes('auth/network-request-failed')) {
          throw new Error('Network error. Please check your connection and try again.');
        } else if (error.message.includes('auth/user-disabled')) {
          throw new Error('This account has been disabled. Please contact support.');
        }
      }
      
      throw error;
    }
  };

  const staffLogin = async (email: string, password: string) => {
    try {
      await setPersistence(auth, browserLocalPersistence);
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;
      const db = getFirestore();
      
      logger.info('Starting staff login search', { 
        uid: firebaseUser.uid,
        email: firebaseUser.email 
      });

      // Check if they're a staff user in any business
      const businessesSnapshot = await getDocs(collection(db, 'businesses'));
      let staffFound = false;

      for (const businessDoc of businessesSnapshot.docs) {
        const businessId = businessDoc.id;
        const staffUserDoc = await getDoc(doc(db, `businesses/${businessId}/staff_users`, firebaseUser.uid));
        
        if (staffUserDoc.exists()) {
          const staffData = staffUserDoc.data();

          // Set the business ID in localStorage
          localStorage.setItem('businessId', businessId);
          localStorage.setItem('facilityId', staffData.facility_id);
          
          // Create the user object
          const userData: StaffUser = {
            uid: firebaseUser.uid,
            email: firebaseUser.email!,
            role: 'staff',
            facilityId: staffData.facility_id,
            businessId: businessId,
            name: staffData.name || firebaseUser.displayName || undefined,
            phone: staffData.phone_number
          };

          setUser(userData);
          staffFound = true;
          return;
        }
      }
      
      if (!staffFound) {
        throw new Error('No staff account found for this email');
      }
    } catch (error) {
      logger.error('Staff login error:', { 
        error: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined
      });
      
      if (error instanceof Error) {
        if (error.message.includes('auth/invalid-credential')) {
          throw new Error('Invalid email or password');
        } else if (error.message.includes('auth/too-many-requests')) {
          throw new Error('Too many failed login attempts. Please try again later.');
        } else if (error.message.includes('auth/network-request-failed')) {
          throw new Error('Network error. Please check your connection and try again.');
        } else if (error.message.includes('auth/user-disabled')) {
          throw new Error('This account has been disabled. Please contact support.');
        }
      }
      
      throw error;
    }
  };

  // Keep the original login function for backward compatibility
  const login = async (email: string, password: string) => {
    try {
      await setPersistence(auth, browserLocalPersistence);
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;
      
      // Set basic user data
      setUser({
        uid: firebaseUser.uid,
        email: firebaseUser.email!,
        role: 'member',
        name: firebaseUser.displayName || undefined
      });
    } catch (error) {
      logger.error('Login error:', { 
        error: error instanceof Error ? error.message : String(error)
      });
      throw error;
    }
  };

  const signup = async (email: string, password: string) => {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      setCurrentUser(userCredential.user);
      return userCredential;
    } catch (error) {
      logger.error('Signup error', { error: error instanceof Error ? error.message : String(error) });
      throw error;
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      setUser(null);
      setCurrentUser(null);
      localStorage.removeItem('authToken');
    } catch (error) {
      logger.error('Logout error', { error: error instanceof Error ? error.message : String(error) });
      throw error;
    }
  };

  const updateUserProfile = async (updatedData: Partial<User>) => {
    if (!currentUser || !user) {
      logger.warn('Cannot update profile: No current user');
      throw new Error('User not authenticated');
    }
    
    try {
      logger.info('Updating user profile', { uid: currentUser.uid });
      const db = getFirestore();
      
      // Only update allowed fields and don't overwrite uid, email, role
      const allowedFields: (keyof User)[] = [
        'firstName', 
        'lastName', 
        'name',
        'stripeSubscriptionId',
        'phone',
      ];
      
      // Filter out fields that shouldn't be updated
      const filteredData: Partial<User> = {};
      Object.keys(updatedData).forEach(key => {
        if (allowedFields.includes(key as keyof User)) {
          // @ts-ignore - We've already checked that the key is valid
          filteredData[key] = updatedData[key];
        }
      });
      
      // Update in Firestore
      const userRef = doc(db, 'users', currentUser.uid);
      await setDoc(userRef, filteredData, { merge: true });
      
      // Update local state
      setUser({
        ...user,
        ...filteredData
      });
      
      logger.info('User profile updated successfully', { uid: currentUser.uid });
    } catch (error) {
      logger.error('Error updating user profile:', { 
        error: error instanceof Error ? error.message : String(error),
        uid: currentUser.uid
      });
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      currentUser, 
      loading, 
      userDataLoading,
      businessData,
      businessFacilities,
      login,
      businessLogin,
      memberLogin,
      staffLogin,
      logout, 
      signup,
      refreshUserData,
      sendVerificationEmail,
      verifyEmail,
      isEmailVerified,
      updateUserProfile
    }}>
      {children}
    </AuthContext.Provider>
  );
}; 